<script lang="ts">
  import { fetchModels, leadLabel, type ModelInfo, type ModelVariable } from '../api/models'
  import { LEAD_WINDOW_IDS, type LeadWindow } from '../map/layers/modelLayer'
  import { LAYERS, MODEL_CHIPS, type LayerDef } from '../map/layers/registry'
  import { isActive, opacityOf, setOpacity, toggleLayer } from '../state/layers.svelte'
  import { layout } from '../state/layout.svelte'
  import { models, setLeadWindow, setRunOverride } from '../state/models.svelte'

  /**
   * The Models group, as a two-level picker.
   *
   * Thirty-seven checkboxes in a phone sheet is not a control. But the
   * registry's rule — a layer is a row, `initLayers()` is synchronous, a
   * persisted id validates in the same tick — is worth more than the flat
   * list, so **the rows stay and the rendering changes**. Nothing here keeps a
   * second list: the chips are derived from `LAYERS` by `def.model`, so a new
   * model is still an entry in the registry's own table.
   *
   * Radio rather than checkbox, because the group is already `exclusive: true`
   * — the flat list has been drawing checkboxes for a radio group since
   * Phase 2.
   *
   * Drawn as round 2 has it (Stage 10.5, 2b): chips, the selected model's
   * fields in two columns with None first, the on field's opacity, then the
   * lead window and the run as segmented bars. `LayerGroup` around it draws
   * the header, the count and the note about the two clocks.
   *
   * A field the deployment does not ingest is absent from the catalog and is
   * not rendered. Before the catalog lands, every row shows: that is one tick
   * of optimism rather than an empty panel, and the catalog arrives in
   * milliseconds off local disk.
   */

  let catalog = $state<ModelInfo[] | null>(null)

  $effect(() => {
    // Re-read when the poller says the catalog moved: the run list is the
    // control someone opens *because* they think a newer run should exist.
    void models.revision
    let cancelled = false
    void fetchModels().then((found) => {
      if (!cancelled) catalog = found
    })
    return () => {
      cancelled = true
    }
  })

  const rows = LAYERS.filter((l): l is LayerDef & { model: string; variable: string } =>
    Boolean(l.model && l.variable),
  )

  /**
   * Chip order is registry order, and nothing keeps a second one.
   *
   * The *short* label, from the same table the rows are generated from: the
   * catalog's is `NBM 2.5 km`, which belongs in the status panel and does not
   * fit a chip in a row of five. Which chips exist is still the catalog's
   * answer — a model this deployment does not ingest has none.
   */
  let chips = $derived(
    MODEL_CHIPS.filter(
      (chip) => catalog === null || catalog.some((m) => m.id === chip.id),
    ),
  )

  /**
   * Which model the list is showing.
   *
   * It follows the active layer, so opening the menu with GFS on screen shows
   * GFS's fields rather than whatever was picked last.
   */
  let picked = $state<string | null>(null)
  let activeRow = $derived(rows.find((r) => isActive(r.id)) ?? null)
  let selected = $derived(
    (picked && chips.some((c) => c.id === picked) ? picked : null) ??
      activeRow?.model ??
      chips[0]?.id ??
      null,
  )

  let info = $derived(catalog?.find((m) => m.id === selected) ?? null)

  /**
   * The rows of the selected model that this deployment actually serves.
   *
   * A plate row needs its field in the catalog; a **vector** row additionally
   * needs the catalog to say that kind exists — a deployment running with
   * `WEATHER_MODEL_VECTORS=""` writes no contours, and a row that turns on and
   * draws nothing is indistinguishable from a quiet day. Same rule as a field
   * nobody ingests, one level down.
   */
  let fields = $derived(
    rows
      .filter((r) => r.model === selected)
      .map((r) => ({ def: r, variable: info?.variables.find((v) => v.id === r.variable) ?? null }))
      .filter(({ def, variable }) => {
        if (catalog === null) return true
        if (!variable) return false
        return !def.vector || (variable.vectors ?? []).includes(def.vector)
      }),
  )

  let runs = $derived(info?.runs ?? [])
  let chosenRun = $derived(selected ? (models.run[selected] ?? '') : '')

  function pick(id: string): void {
    picked = id
  }

  /** "12Z · 2 h ago", or "12Z · to +6 h" when the run is still publishing.
   *  The segment shows the cycle; this is its tooltip. */
  function runLabel(run: { run: string; complete: boolean; contiguous_lead: number | null }): string {
    const cycle = `${run.run.slice(9, 11)}Z`
    const at = runInstant(run.run)
    const age = at ? ageLabel(Date.now() - at.getTime()) : ''
    const reach =
      run.complete || run.contiguous_lead === null
        ? ''
        : ` · to ${leadLabel(run.contiguous_lead)}`
    return `${cycle}${age ? ` · ${age}` : ''}${reach}`
  }

  function runInstant(run: string): Date | null {
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})Z$/.exec(run)
    if (!m) return null
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4])))
  }

  function ageLabel(ms: number): string {
    const minutes = Math.max(0, Math.round(ms / 60_000))
    if (minutes < 60) return `${minutes} m ago`
    const hours = Math.floor(minutes / 60)
    return `${hours} h ago`
  }

  /** What a vector row draws, for the radio label. The catalog names the
   *  *field*; this names the artifact. */
  const VECTOR_NAMES: Record<'contour' | 'barb', string> = {
    contour: 'contours',
    barb: 'barbs',
  }

  /**
   * The row's whole label, as one string.
   *
   * One text node rather than a styled suffix in its own `<span>`, and that is
   * not a style preference: Svelte collapses the whitespace between an
   * expression and an element, so the suffix rendered as
   * "Mean sea level pressurecontours" — correct-looking in the markup and
   * wrong on screen, which is what `make e2e` is for and how this was caught.
   * It also makes the accessible name a sentence, which is what the e2e
   * picker step matches on.
   */
  function rowLabel(def: LayerDef, variable: ModelVariable | null): string {
    const base = variable?.label ?? def.label
    return def.vector ? `${base} ${VECTOR_NAMES[def.vector]}` : base
  }

  /** A barb reads in knots whatever the plate's legend says, and the catalog
   *  states that rather than letting this file assume it. */
  function unitsFor(def: LayerDef, variable: ModelVariable): string {
    if (def.vector === 'barb') return variable.barb?.units ?? variable.units
    return variable.units
  }

  /**
   * The run bar: Latest (which follows new runs) and then the older runs
   * newest first, as many as fit a bar of five. A pinned run that fell off
   * the end is kept in, or the bar would show nothing selected.
   */
  let runChoices = $derived.by(() => {
    const older = [...runs]
      .filter((r) => r.run !== info?.latest)
      .sort((a, b) => (a.run < b.run ? 1 : -1))
    const shown = older.slice(0, 4)
    const pinned = older.find((r) => r.run === chosenRun)
    if (pinned && !shown.includes(pinned)) shown[shown.length - 1] = pinned
    return shown
  })

  let latestRun = $derived(runs.find((r) => r.run === info?.latest) ?? null)

  function pct(id: string): number {
    return Math.round(opacityOf(id) * 100)
  }

  const WINDOW_LABELS: Record<LeadWindow, string> = {
    '3h': '+3 h',
    '6h': '+6 h',
    '12h': '+12 h',
    '48h': '+48 h',
    all: 'All',
  }
</script>

<div class="picker" class:phone={!layout.isDesktop}>
  {#if chips.length === 0}
    <p class="empty">No model data on this server.</p>
  {:else}
    <!-- On a phone six chips do not fit across at 40 px each, so the row
         scrolls sideways, which is the sheet's answer to anything wider than
         the screen. The desktop panel wraps them. -->
    <div class="chips" role="tablist" aria-label="Model">
      {#each chips as chip (chip.id)}
        <button
          type="button"
          role="tab"
          class="chip touch-target"
          class:on={chip.id === selected}
          aria-selected={chip.id === selected}
          onclick={() => pick(chip.id)}
        >
          {chip.label}
        </button>
      {/each}
    </div>

    <div class="block">
      <div class="block-head">
        <span class="eyebrow">{chips.find((c) => c.id === selected)?.label ?? ''} fields</span>
        <span class="hint">{fields.length} fields</span>
      </div>
      <div class="fields" role="radiogroup" aria-label="Field">
        <!-- The group is exclusive, so "none" is a real choice and needs to be
             reachable, the way SPC outlooks draw it. -->
        <label class="field touch-target">
          <input
            type="radio"
            class="wx-box"
            name="model-field"
            checked={activeRow === null || activeRow.model !== selected}
            onchange={() => activeRow && toggleLayer(activeRow.id)}
          />
          <span class="name">None</span>
        </label>
        {#each fields as { def, variable } (def.id)}
          <label class="field touch-target">
            <input
              type="radio"
              class="wx-box"
              name="model-field"
              checked={isActive(def.id)}
              onchange={() => toggleLayer(def.id)}
            />
            <!-- The catalog's label, because the backend registry is what
                 coloured the pixels. The static one is the fallback for the
                 tick before the catalog lands. A vector row adds which artifact
                 of that field it draws, which is the one thing the catalog's
                 field label cannot say. The units are the legend's to state. -->
            <span class="name" title={variable ? unitsFor(def, variable) : undefined}
              >{rowLabel(def, variable)}</span
            >
          </label>
        {/each}
      </div>
      {#if activeRow}
        <div class="opacity">
          <span class="hint grow">{activeRow.label} · opacity</span>
          <input
            type="range"
            class="wx-range"
            style:--pct="{pct(activeRow.id)}%"
            min="0"
            max="100"
            step="5"
            value={pct(activeRow.id)}
            aria-label="{activeRow.label} opacity"
            oninput={(e) => setOpacity(activeRow!.id, Number(e.currentTarget.value) / 100)}
          />
          <span class="pct">{pct(activeRow.id)}%</span>
        </div>
      {/if}
    </div>

    <!-- A `div` with a group role, not a `label`: a `<label>` names its first
         labellable descendant, so wrapping these buttons in one gave the
         first of them the accessible name "Look ahead +6 h +12 h +48 h All".
         Caught by `make e2e`, which could not find a button called "+3 h". -->
    <div class="block" role="group" aria-label="Lead window">
      <span class="eyebrow" aria-hidden="true">Lead window</span>
      <span class="seg">
        {#each LEAD_WINDOW_IDS as id (id)}
          <button
            type="button"
            class="touch-target"
            class:on={models.window === id}
            aria-pressed={models.window === id}
            onclick={() => setLeadWindow(id)}
          >
            {WINDOW_LABELS[id]}
          </button>
        {/each}
      </span>
    </div>

    {#if runs.length > 0 && selected}
      <!-- Not persisted: retention deletes a run within hours, and a restored
           selection would boot the app pointed at a 404. -->
      <div class="block" role="group" aria-label="Run">
        <span class="eyebrow" aria-hidden="true">Run</span>
        <span class="seg runs">
          <button
            type="button"
            class="latest touch-target"
            class:on={chosenRun === ''}
            aria-pressed={chosenRun === ''}
            title={latestRun ? runLabel(latestRun) : undefined}
            onclick={() => setRunOverride(selected!, null)}
          >
            Latest{info?.latest ? ` · ${info.latest.slice(9, 11)}Z` : ''}
          </button>
          {#each runChoices as run (run.run)}
            <button
              type="button"
              class="touch-target"
              class:on={chosenRun === run.run}
              aria-pressed={chosenRun === run.run}
              aria-label={runLabel(run)}
              title={runLabel(run)}
              onclick={() => setRunOverride(selected!, run.run)}
            >
              {run.run.slice(9, 11)}Z
            </button>
          {/each}
        </span>
      </div>
    {/if}
  {/if}
</div>

<style>
  .picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 var(--px, 16px) 4px;
    --chip: 28px;
    --seg: 28px;
    --frow: 27px;
  }

  .picker.phone {
    gap: 8px;
    --chip: 40px;
    --seg: 40px;
    --frow: 40px;
  }

  .empty {
    font-size: 12px;
    color: var(--color-muted);
    margin: 4px 0;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .phone .chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    /* The scroll is horizontal on purpose; a vertical drag belongs to the
       sheet underneath. */
    touch-action: pan-x;
    scrollbar-width: none;
  }

  .phone .chips::-webkit-scrollbar {
    display: none;
  }

  .chip {
    flex: none;
    height: var(--chip);
    min-height: var(--chip);
    padding: 0 10px;
    border: 2px solid var(--color-divider);
    background: none;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }

  .chip.on {
    background: var(--color-text);
    border-color: var(--color-text);
    color: var(--color-bg);
    font-weight: 800;
  }

  .block {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .block-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .hint {
    font-size: 11px;
    color: var(--color-muted);
  }

  .fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 12px;
    border-top: 1px solid var(--color-divider);
  }

  .field {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: var(--frow);
    border-bottom: 1px solid var(--color-divider);
    cursor: pointer;
  }

  .name {
    flex: 1;
    min-width: 0;
    font-size: var(--fs, 13px);
    line-height: 1.2;
  }

  .opacity {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: var(--row, 32px);
  }

  .opacity .hint {
    font-size: 12px;
  }

  .grow {
    flex: 1;
    min-width: 0;
  }

  .opacity .wx-range {
    width: 96px;
  }

  .pct {
    width: 30px;
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .seg {
    display: flex;
    border: 2px solid var(--color-text);
  }

  .seg button {
    flex: 1 1 0;
    min-width: 0;
    height: var(--seg);
    min-height: var(--seg);
    padding: 0 8px;
    border: 0;
    background: none;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    text-align: left;
    cursor: pointer;
  }

  .seg button + button {
    border-left: 1px solid var(--color-divider);
  }

  .seg button.latest {
    flex-grow: 2;
  }

  .seg button.on {
    background: var(--color-text);
    color: var(--color-bg);
    font-weight: 800;
  }

  .seg button:focus-visible {
    outline-offset: -4px;
  }
</style>
